import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { ArrowUpRight, DownloadSimple } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

const LEAD_STATUSES = [
  "new", "reviewing", "contacted", "qualified", "consultation_requested",
  "consultation_booked", "won", "not_now", "closed",
];

const SOURCE_COLORS = ["#E11D48", "#0A0A0A", "#71717A", "#F59E0B", "#10B981", "#3B82F6", "#D4D4D8"];

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats/overview").then(({ data }) => setStats(data));
  }, []);

  if (!stats) {
    return <div className="p-10 font-mono text-sm text-zinc-500" data-testid="dashboard-loading">Loading…</div>;
  }

  const { kpis, funnel, sources, demo_trends, recent_leads } = stats;
  const funnelData = LEAD_STATUSES.map((s) => ({ name: s.replace(/_/g, " "), value: funnel[s] || 0 }));

  return (
    <div className="p-8 md:p-10" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="overline mb-2">Dashboard</p>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Overview</h1>
        </div>
        <a
          href={`${API_BASE}/admin/backup`}
          target="_blank"
          rel="noreferrer"
          className="btn-ghost-ink !py-2"
          data-testid="dashboard-backup"
          title="Download a gzipped JSONL snapshot of every collection in the database"
        >
          <DownloadSimple size={14} weight="bold" /> Backup database
        </a>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-0 border-t border-l border-zinc-200 mb-8">
        <Kpi label="Total leads" value={kpis.total_leads} testid="kpi-total-leads" />
        <Kpi label="New" value={kpis.new_leads} highlight testid="kpi-new-leads" />
        <Kpi label="Last 7 days" value={kpis.leads_7d} testid="kpi-leads-7d" />
        <Kpi label="Consultations" value={kpis.consultation_requests} testid="kpi-consultations" />
        <Kpi label="Active promos" value={kpis.active_promotions} testid="kpi-promos" />
        <Kpi label="Conversion rate" value={`${kpis.conversion_rate}%`} testid="kpi-conversion" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-white border border-zinc-200 p-6" data-testid="funnel-chart">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold">Lead funnel</h2>
            <Link to="/admin/leads" className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
              View all <ArrowUpRight size={12} />
            </Link>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={funnelData}>
              <CartesianGrid vertical={false} stroke="#E4E4E7" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#71717A" }} interval={0} angle={-15} textAnchor="end" height={60} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#71717A" }} />
              <Tooltip cursor={{ fill: "#F4F4F5" }} />
              <Bar dataKey="value" fill="#0A0A0A" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-zinc-200 p-6" data-testid="sources-chart">
          <h2 className="text-base font-semibold mb-4">Lead sources</h2>
          {sources.length === 0 ? (
            <p className="text-sm text-zinc-500 font-mono py-12 text-center">No leads yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={sources} dataKey="count" nameKey="source" cx="50%" cy="50%" innerRadius={40} outerRadius={80}>
                  {sources.map((_, i) => <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-zinc-200" data-testid="recent-leads">
          <div className="p-6 border-b border-zinc-200 flex items-center justify-between">
            <h2 className="text-base font-semibold">Recent leads</h2>
            <Link to="/admin/leads" className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900">View all</Link>
          </div>
          {recent_leads.length === 0 ? (
            <p className="text-sm text-zinc-500 font-mono p-6">No leads yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-left">
                  <th className="px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Name</th>
                  <th className="px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Company</th>
                  <th className="px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {recent_leads.map((l) => (
                  <tr key={l.id} className="border-t border-zinc-200 hover:bg-zinc-50">
                    <td className="px-6 py-3">
                      <Link to={`/admin/leads/${l.id}`} className="font-medium hover:underline">{l.name || l.email}</Link>
                      <div className="text-xs text-zinc-500">{l.email}</div>
                    </td>
                    <td className="px-6 py-3 text-zinc-600">{l.company || "—"}</td>
                    <td className="px-6 py-3"><span className="status-pill">{(l.status || "new").replace(/_/g, " ")}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-white border border-zinc-200" data-testid="demo-trends">
          <div className="p-6 border-b border-zinc-200"><h2 className="text-base font-semibold">Demo interest trends</h2></div>
          {demo_trends.length === 0 ? (
            <p className="text-sm text-zinc-500 font-mono p-6">No interest tracked yet.</p>
          ) : (
            <ul className="divide-y divide-zinc-200">
              {demo_trends.map((d, i) => (
                <li key={i} className="px-6 py-4 flex items-center justify-between">
                  <span className="font-medium">{d.title}</span>
                  <span className="font-mono text-sm text-zinc-600">{d.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, highlight, testid }) {
  return (
    <div className={`border-r border-b border-zinc-200 p-5 ${highlight ? "bg-white" : "bg-white"}`} data-testid={testid}>
      <p className="overline text-zinc-500 mb-2">{label}</p>
      <p className={`text-3xl font-bold tracking-tight ${highlight ? "text-[hsl(var(--appistry-signal))]" : "text-zinc-950"}`} style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</p>
    </div>
  );
}
